/**
 * Trazabilidad global del sistema (`/api/auditorias`).
 *
 * Registro de todas las acciones de escritura de la aplicación, alimentado por
 * listeners comodín del backend. Solo lectura y exportación: la bitácora no se
 * edita ni se borra, porque un registro modificable no sirve como evidencia.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import type AuditoriaEvaluacion from '@/interfaces/AuditoriaEvaluacion'
import { http, getAuthToken } from '@/services/http'

const API = process.env.VUE_APP_API_BASE_URL

/**
 * Consulta auditoria · `GET /api/evaluaciones-indicadores`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `[]`. Un array
 * vacío NO distingue «sin datos» de «la petición falló».
 *
 * @returns AuditoriaEvaluacion[]
 */
export async function getAuditoriaEvaluacionService(idEvaluacion: number): Promise<AuditoriaEvaluacion[]> {
  const result = await http.get<any>(`/api/evaluaciones-indicadores/${idEvaluacion}/auditoria`)
  if (!result.ok) return []
  const d = result.data
  return Array.isArray(d) ? d : d?.data ?? []
}

/**
 * Consulta auditoria · `GET /api/versiones`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `[]`. Un array
 * vacío NO distingue «sin datos» de «la petición falló».
 *
 * @returns AuditoriaEvaluacion[]
 */
export async function getAuditoriaVersionService(idVersion: number): Promise<AuditoriaEvaluacion[]> {
  const result = await http.get<any>(`/api/versiones/${idVersion}/auditoria-evaluaciones`)
  if (!result.ok) return []
  const d = result.data
  return Array.isArray(d) ? d : d?.data ?? []
}

// Devuelve un Blob (CSV/JSON) — no pasa por http.ts, se inyecta el token manualmente
export async function exportarAuditoriaService(idVersion: number, formato: 'json' | 'csv'): Promise<Blob | null> {
  try {
    const token = getAuthToken()
    const headers: Record<string, string> = { Accept: formato === 'csv' ? 'text/csv' : 'application/json' }
    if (token) headers['Authorization'] = `Bearer ${token}`
    const resp = await fetch(
      `${API}/api/versiones/${idVersion}/auditoria-evaluaciones/exportar?formato=${formato}`,
      { headers }
    )
    if (!resp.ok) return null
    return await resp.blob()
  } catch (err) {
    console.error('[AuditoriaService] Error al exportar:', err)
    return null
  }
}
